import React, { useCallback, useMemo, useRef, useState } from "react";
import { connect } from "react-redux";
import { createStore } from 'devextreme-aspnet-data-nojquery';
import DataSource from "devextreme/data/data_source";
import data from "../../../../assets/apiRoutes.json";
import { GridLayout } from "../../../../common/GridLayout";
import GridLayoutManager from "../../../../gridLayoutCustomization/GridLayoutManager";
import { GRID_PAGE_SIZES, CURRENCY_FORMAT, DEFAULT_LAYOUT_NAME } from "../constants";
import {
    Column,
    DataGrid,
    GroupPanel,
    Grouping,
    FilterRow,
    FilterPanel,
    SearchPanel,
    Sorting,
    Paging,
    Selection,
    Toolbar,
    Item,
    Export,
    Pager,
    ColumnChooser,
    ColumnChooserSearch,
    StateStoring,
} from "devextreme-react/data-grid";

/**
 * A/R open items for one customer, backed by ar/OpenItem/GetOpenItems -
 * a purpose-built endpoint/DTO (CustomerOpenItemDTO) rather than the shared,
 * detail-flattening ar/OpenItem/GetOpenItems used by other AR screens, so this
 * tab gets real server-side paging/sort/filter like its sibling tabs.
 *
 * The AI8 query (qryAROpenItems) pins COMPANYID + MODULE='AR' + RECORDTYPE='U'
 * alongside the customer, and orders by TransactionNumber then KeySequence -
 * all reproduced server-side (see OpenItemService.GetOpenItems).
 */
const AROpenItemsTab = (props) => {
    const { customerId, gridKey } = props;
    const accessToken = props.oidc.user.access_token;
    const dataGrid = useRef(null);

    // Named grid layouts: GridLayoutManager switches currentLayout, and the state
    // storage requests carry it so each layout keeps its own saved state.
    const [currentLayout, setCurrentLayout] = useState(DEFAULT_LAYOUT_NAME);
    const loadState = useCallback(() => GridLayout.sendStorageRequest(gridKey, "GET", accessToken, null, currentLayout), [gridKey, currentLayout]);
    const saveState = useCallback((state) => GridLayout.sendStorageRequest(gridKey, "POST", accessToken, state, currentLayout), [gridKey, currentLayout]);

    const dataSource = useMemo(() => {
        if (!customerId) {
            return [];
        }
        const store = createStore({
            key: 'RowId',
            loadParams: { CustomerRowId: customerId },
            loadUrl: `${window._env_.REACT_APP_APIS}/${data.AR.GetCustomerOpenItems}`,
            onBeforeSend: (method, ajaxOptions) => {
                ajaxOptions.headers = {
                    access_token: accessToken,
                    Authorization: `Bearer ${accessToken}`,
                };
            },
        });
        const source = new DataSource({ store: store });
        source.sort([
            { selector: 'TransactionNumber', desc: false },
            { selector: 'KeySequence', desc: false },
        ]);
        return source;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [customerId]);

    return (
        <DataGrid
            onExporting={(e) => {
                if (window.handleDevExtremeDataGridExport) {
                    window.handleDevExtremeDataGridExport(e);
                }
            }}
            ref={dataGrid}
            className="tabbed-detail-grid"
            dataSource={dataSource}
            keyExpr="RowId"
            allowColumnReordering={true}
            allowColumnResizing={true}
            showBorders={true}
            hoverStateEnabled={true}
            columnAutoWidth={true}
            remoteOperations={true}
        >
            <ColumnChooser enabled={true} title="Choose Column" width={290} height={290} sortOrder="asc">
                <ColumnChooserSearch enabled={true} editorOptions={{ placeholder: 'Search column' }} />
            </ColumnChooser>
            <Export enabled={true} allowExportSelectedData={true} />
            <Selection mode="single" />
            <Sorting visible={true} mode="multiple" />
            <Paging defaultPageSize={10} />
            <Pager visible={true} showPageSizeSelector={true} allowedPageSizes={GRID_PAGE_SIZES} showNavigationButtons={true} />
            <FilterRow visible={true} />
            <FilterPanel visible={true} />
            <SearchPanel visible={true} width={240} placeholder="Search..." />
            <GroupPanel visible={true} />
            <Grouping autoExpandAll={false} />
            <StateStoring enabled={true} type="custom" customLoad={loadState} storageKey={gridKey} customSave={saveState} />

            {/* Default-visible columns - the AI8 AR Open Items grid (ARD0011 hosted in
                OMD0010) shows Transaction #, Customer, Type, Transaction, Due Date,
                Balance and Comment. Record # is kept visible as the project standard. */}
            <Column dataField="RecordNumber" caption="Record #" />
            <Column dataField="TransactionNumber" caption="Transaction #" />
            <Column dataField="CustomerCode" caption="Customer" />
            <Column dataField="TransactionTypeDescription" caption="Type" />
            <Column dataField="TransactionDate" caption="Transaction" dataType="date" />
            <Column dataField="TransactionDueDate" caption="Due Date" dataType="date" />
            <Column dataField="Balance" caption="Balance" dataType="number" format={CURRENCY_FORMAT} />
            <Column dataField="Comment" caption="Comment" />

            {/* Column chooser (hidden by default) - matches the AI8 Columns list */}
            <Column dataField="CustomerName" caption="Name" visible={false} />
            <Column dataField="ReferenceNumber" caption="Reference #" visible={false} />
            <Column dataField="InvoiceNumber" caption="Invoice #" visible={false} />
            <Column dataField="StatusDescription" caption="Status" visible={false} />
            <Column dataField="Description" caption="Description" visible={false} />
            <Column dataField="DiscountAvailableAmount" caption="Discount Available" dataType="number" format={CURRENCY_FORMAT} visible={false} />
            <Column dataField="DiscountAvailableDate" caption="Discount Available Date" dataType="date" visible={false} />
            <Column dataField="ProfileCode" caption="Profile" visible={false} />
            <Column dataField="FiscalPeriod" caption="Fiscal Period" dataType="number" visible={false} />
            <Column dataField="FiscalYear" caption="Fiscal Year" dataType="number" visible={false} />
            <Column dataField="CommitDate" caption="Commit Date" dataType="date" visible={false} />
            <Column dataField="CreationUserId" caption="Created By" visible={false} />
            <Column dataField="CreationDateTime" caption="Creation Date" dataType="datetime" visible={false} />
            <Column dataField="LastUpdateUserId" caption="Last Updated By" visible={false} />
            <Column dataField="LastUpdateDateTime" caption="Last Updated" dataType="datetime" visible={false} />
            <Column dataField="Adjustments" caption="Adjustments" dataType="number" format={CURRENCY_FORMAT} visible={false} />
            <Column dataField="CalculatedPaid" caption="Calculated Paid" dataType="number" format={CURRENCY_FORMAT} visible={false} />
            <Column dataField="KeySequence" caption="Key Sequence" dataType="number" visible={false} />
            <Column dataField="OriginalAmount" caption="Original Amount" dataType="number" format={CURRENCY_FORMAT} visible={false} />

            {/* Technical columns stay hidden and out of the chooser */}
            <Column dataField="RowId" caption="Row Id" visible={false} showInColumnChooser={false} />
            <Column dataField="RCustomer" caption="Customer Id" visible={false} showInColumnChooser={false} />
            <Column dataField="RecordType" caption="Record Type" visible={false} showInColumnChooser={false} />
            <Column dataField="StatusFlag" caption="Status Flag" visible={false} showInColumnChooser={false} />
            <Column dataField="TransactionType" caption="Type Flag" visible={false} showInColumnChooser={false} />
            <Toolbar>
                <Item location="after">
                    <GridLayoutManager gridKey={gridKey} dataGrid={dataGrid} setCurrentLayout={setCurrentLayout} />
                </Item>
                <Item name="exportButton" />
                <Item name="columnChooserButton" />
            </Toolbar>
        </DataGrid>
    );
};

function mapStateToProps(state) {
    return {
        oidc: state.oidc,
    };
}
export default connect(mapStateToProps)(AROpenItemsTab);
